import { SendDelayEntry } from "@/pages/Private/pages/Company/schema/copywritingSettings";

/**
 * The company's "Send Delay Defaults" copywriting setting (PREC-4299): the delay, in days, that a
 * newly added outreach step starts with, by its step number. The settings page shows these rows
 * and the campaign builders apply them; both read this module so they cannot disagree.
 */

/** What a new step gets when nothing is configured for its number — the builders' old default. */
export const DEFAULT_NEW_STEP_DAYS = 1;

/**
 * The first step goes out in the next sending window; its delay is never applied, so the setting
 * starts at step 2 and a stored step 1 row is ignored.
 */
export const FIRST_CONFIGURABLE_STEP = 2;

/** The rows the settings page starts from when the company has set nothing. */
const DEFAULT_DISPLAYED_STEPS = [2, 3, 4];

const isUsableDelay = (days: unknown): days is number =>
	typeof days === "number" && Number.isFinite(days) && days >= 0;

/** The delay a new outreach step with this number starts with. */
export const configuredDelayForStep = (
	defaults: SendDelayEntry[] | null | undefined,
	step: number
): number => {
	if (step < FIRST_CONFIGURABLE_STEP) {
		return DEFAULT_NEW_STEP_DAYS;
	}

	const entry = defaults?.find((row) => row.step === step);

	return isUsableDelay(entry?.delayDays) ? entry.delayDays : DEFAULT_NEW_STEP_DAYS;
};

/**
 * The rows the settings page shows: the stored ones from step 2 on, in step order — or, when none
 * are stored, the delay that applies today (1 day for steps 2 to 4), so the page never shows a
 * delay that is not applied.
 */
export const displayedDelayRows = (
	defaults: SendDelayEntry[] | null | undefined
): SendDelayEntry[] => {
	const stored = (defaults ?? [])
		.filter((row) => row.step >= FIRST_CONFIGURABLE_STEP)
		.sort((a, b) => a.step - b.step);

	if (stored.length > 0) {
		return stored;
	}

	return DEFAULT_DISPLAYED_STEPS.map((step) => ({ step, delayDays: DEFAULT_NEW_STEP_DAYS }));
};
